<template>
  <div ref="elRef" :class="$style.sidebarCustomer">
    <template v-if="isDropdownShown">
      <div
        data-cy="agency-switcher"
        :class="[
          $style.sidebarCustomerSelected,
          $style.toggleDropdown,
          { [$style.selectable]: isDropdownShown },
          { [$style.ifDropdownOpen]: !isMenuHidden },
        ]"
        :aria-expanded="!isMenuHidden ? 'true' : 'false'"
        aria-controls="sidebar-customer-dropdown-menu"
        aria-haspopup="true"
        role="button"
        tabindex="0"
        @keyup.enter="onToggleIfMoreThanOneAgency"
        @click="onToggleIfMoreThanOneAgency"
      >
        <AgencyItem :key="agency.customerId" :class="$style.sidebarCustomerItem" :agency="agency" />
        <div v-if="isDropdownShown" :class="$style.icon">
          <HgIcon :icon="dropdownIcon" size="medium" theme="current" />
        </div>
      </div>
      <ul
        id="sidebar-customer-dropdown-menu"
        :class="$style.sidebarCustomerDropdown"
        :hidden="isMenuHidden"
      >
        <!-- This button only appears on small screens -->
        <li :class="$style.toggleDropdownBack">
          <div
            role="button"
            :class="$style.toggleDropdown"
            @keyup.enter="toggleDropdown"
            @click="toggleDropdown"
          >
            <HgIcon icon="ArrowLeft" size="large" theme="current" /><span>{{
              t('$domus.backToMenu')
            }}</span>
          </div>
        </li>
        <li v-for="inactiveAgency in inactiveAgencies" :key="`${inactiveAgency.customerId}`">
          <div
            :data-cy="`agency-switcher-option-${inactiveAgency.customerId}`"
            :class="[$style.sidebarItem, $style.selectable]"
            tabindex="0"
            role="button"
            @keyup.enter="onItemSelect(inactiveAgency)"
            @click="onItemSelect(inactiveAgency)"
          >
            <AgencyItem :class="$style.sidebarCustomerItem" :agency="inactiveAgency" />
            <div :class="$style.separator"></div>
          </div>
        </li>
      </ul>
    </template>
    <div v-else :class="$style.sidebarCustomerSelected">
      <AgencyItem :class="$style.sidebarCustomerItem" :agency="agency" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgIcon, Icon } from '@smg-real-estate/domus';
import { computed, type PropType } from 'vue';

import type { AgencyDetails } from '@/types-agency';

import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import AgencyItem from './AgencyItem.vue';
import { useDropdownHandler } from './mixins/useDropdownHandler';

const store = useStore();
const { t } = useI18n();
const { elRef, isMenuHidden, toggleDropdown, closeDropdown } = useDropdownHandler();

const { agency } = defineProps({
  agency: {
    type: Object as PropType<AgencyDetails>,
    default: () => {},
  },
});
const emit = defineEmits(['agencySwitched']);

const inactiveAgencies = computed(() => store.getters.getAgenciesWithoutSelectedAgency);

const isDropdownShown = computed(() => !!inactiveAgencies.value?.length);

const dropdownIcon = computed(() => {
  return isMenuHidden.value ? Icon.ChevronDown : Icon.ChevronUp;
});

const onToggleIfMoreThanOneAgency = () => {
  if (isDropdownShown.value) {
    toggleDropdown();
  }
};

const onItemSelect = (customer: AgencyDetails) => {
  closeDropdown();
  emit('agencySwitched', customer);
};
</script>

<style lang="scss" module>
@import '@smg-real-estate/domus/style-tokens-mixins.scss';
@import './styles';

$sidebar-customer-item-width: 11.75rem; // 188px

.sidebarCustomer {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  color: $color-white;

  @media (min-width: $viewport-sm-min) {
    position: relative;
    width: 100%;
  }
}

.ifDropdownOpen {
  background-color: $color-deep-blue-90;
}

.sidebarCustomerSelected {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 2 * #{$space-75});
  height: 100%;
  padding: $space-75;

  .icon {
    flex-shrink: 0;
  }

  .sidebarCustomerItem {
    width: $sidebar-customer-item-width;
  }

  &.toggleDropdown {
    gap: $space-50;
  }

  &.selectable {
    cursor: pointer;
  }

  @media (min-width: $viewport-sm-min) {
    width: calc(100% - 2 * #{$space-150});
    padding: $space-100 $space-150;
  }
}

.sidebarItem {
  width: 100%;
  height: 100%;

  &.selectable {
    cursor: pointer;
  }

  &:hover {
    background-color: $color-deep-blue-100;
  }
}

.sidebarCustomerDropdown {
  @include menu-ul-reset;

  & {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 19;
    width: 100%;
    overflow-y: auto;
    background-color: $color-deep-blue-90;
  }

  li {
    .separator {
      height: 1px;
      margin: 0 22px 0 16px;
      background-color: $color-deep-blue-70;
    }
  }

  li:last-child {
    .separator {
      height: 0;
    }
  }

  .sidebarCustomerItem {
    padding: $space-100 $space-75;
  }

  .toggleDropdownBack {
    @include sidebar-section-top;

    & {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: $color-deep-blue-90;
    }
  }

  .toggleDropdown {
    display: flex;
    flex: 1;
    align-items: center;
    height: auto;
    padding: $space-100 $space-75;
    cursor: pointer;

    > span {
      margin-left: 5px;
    }
  }

  @media (min-width: $viewport-sm-min) {
    position: absolute;
    top: calc(100% - 6px);
    bottom: auto;
    left: $space-150;
    z-index: 9;
    max-height: 25rem;
    border-radius: $border-radius-l;
    box-shadow: $elevation-high;

    .toggleDropdownBack {
      display: none;
    }
  }
}
</style>
